import { Module, VuexModule, Mutation, Action, getModule } from "vuex-module-decorators";
import { formatBool, formatDate } from "../../../../utils";
import { TableField, PageData } from "../../../types";
import {
    GetBarcodeOrdersRequest,
    BarcodeOrderListFilters,
    BarcodeOrderResource,
    BarcodeOrderListItem,
    BarcodeOrderItemListItem,
    StockPickingListItem,
    BarcodeOrderItemPickListItem,
    DeliverySignatureResource,
    OrderItemGps,
    BarcodeNoScanLineItemListItem
  } from "../../../../models/api/barcodeSystem/barcodeOrders/get";
import { PalletListItem } from "../../../../models/api/barcodeSystem/inventory/pallets";
import Store from "../../../index";
import { rawApi, api } from "../../../../lib";
import { AxiosResponse } from "axios";
import { defaultBarcodeOrder, defaultBarcodeOrderFilters } from "./helpers";
import BarcodeSystemModule from "@/store/modules/barcodeSystem";
import notifications from "@/lib/notifications";
import _ from "lodash";
import * as modals from "@/lib/modals";
import { ReceiveBarcodeOrderRequest } from "../../../../models/api/barcodeSystem/barcodeOrders/receive";
import { DeliverBarcodeOrderRequest } from "../../../../models/api/barcodeSystem/barcodeOrders/deliver";
import { RereceiveBarcodeOrderRequest } from "../../../../models/api/barcodeSystem/barcodeOrders/rereceive";
import { UpdateBarcodeOrderItemRequest } from "../../../../models/api/barcodeSystem/barcodeOrders/updateBarcodeOrderItem";
import { CreateOrderFromHeaderNumberRequest, CreateOrderFromHeaderNumberResponse } from "../../../../models/api/barcodeSystem/barcodeOrders/createOrderFromHeaderNumber";
import router from "../../../../router";
import { LinkInventoryRequest } from "../../../../models/api/barcodeSystem/barcodeOrders/linkInventory";
import { TransactionResouce } from "@/models/api/barcodeSystem/transactions/get";
import { UpdateBarcodeNoScanLineItemRequest } from "@/models/api/barcodeSystem/barcodeOrders/updateBarcodeNoScanLineItem";
import { CreateBarcodeNoScanLineItemRequest } from "@/models/api/barcodeSystem/barcodeOrders/createBarcodeNoScanLineItem";
import { DeleteBarcodeNoScanLineItemRequest } from "@/models/api/barcodeSystem/barcodeOrders/deleteBarcodeNoScanLineItem";
import { RequiredStockItemResource, SaveRequiredStockRequest } from "@/models/api/barcodeSystem/barcodeOrders/requiredStock";

const name = "barcodeOrder";
const barcodeSystemModuleState = getModule(BarcodeSystemModule);

if (Store.state[name]) {
  Store.unregisterModule(name);
}

@Module({ name: name, dynamic: true, store: Store, namespaced: true })
export default class BarcodeOrderModule extends VuexModule {
    public currentBarcodeOrder: BarcodeOrderResource = defaultBarcodeOrder();
    public currentBarcodeOrderItems: BarcodeOrderItemListItem[] | null = null;    
    public currentBarcodeNoScanLineItems: BarcodeNoScanLineItemListItem[] | null = null; 
    public currentBarcodeOrderItemPicks: BarcodeOrderItemPickListItem[] | null = null;
    public currentBarcodeOrderStockPickings: StockPickingListItem[] | null = null;
    public currentBarcodeOrderPallets: PalletListItem[] | null = null;
    public currentBarcodeDeliverySignatures: DeliverySignatureResource[] | null = null;
    public currentBarcodeOrderTransactions: TransactionResouce[] | null = null;
    public data?: BarcodeOrderListItem[] | null = null;
    public filters: BarcodeOrderListFilters = defaultBarcodeOrderFilters();
    public dataSortKey: string | null = null;
    public dataSortAscending: boolean = true;
    public pageData: PageData = {
      totalPages: null,
      totalItems: null,
      currentPage: 1,
      pageSize: 50
    };
    public fields: Array<TableField> = [
        { key: "selected", label: "", hidden: false },
        { key: "orderNumber", sortable: true, label: "SYS #", hidden: false },
        { key: "crn", sortable: true, label: "CRN", hidden: false },
        { key: "facilityCode", sortable: true, label: "Facility", hidden: false },
        { key: "company.companyName", sortable: true, label: "Company", hidden: false },
        { key: "displayBillToCompanyId", sortable: true, label: "Bill To", hidden: false },
        { key: "statusDescription", sortable: true, label: "Status", hidden: false},
        { key: "receivedDate", sortable: true, label: "Received Date", formatter: formatDate, hidden: false, noWrap: true},
        { key: "loadedDate", sortable: true, label: "Loaded Date", formatter: formatDate, hidden: false, noWrap: true},
        { key: "deliveredDate", sortable: true, label: "Delivered Date", formatter: formatDate, hidden: false, noWrap: true}
    ];
    public loading: boolean = false;

    @Action
    async getBarcodeOrders(params: GetBarcodeOrdersRequest): Promise<void> {
      const pageSize = params ? params.pageSize : null || this.pageData.pageSize || 30;
      const pageNumber = params ? params.pageNumber : null || this.pageData.currentPage + 1;

      if (pageNumber === 1) {
        this.resetList();
      }
  
      let target = `/barcode/orders/?page=${pageNumber}&PageSize=${pageSize}`;
  
      if (this.filters) {
        if (this.filters.id) {
          target += `&id=${this.filters.id}`;
        }
        if (this.filters.loadId) {
          target += `&loadId=${this.filters.loadId}`;
        }
        if (this.filters.orderNumber) {
          target += `&orderNumber=${this.filters.orderNumber}`;
        }
        if (this.filters.crn) {
            target += `&crn=${this.filters.crn}`;
        }
        if (this.filters.statuses) {
          this.filters.statuses.forEach(element => {
            target += `&statuses=${element}`;
          });
        }
        if (this.filters.deliveryTypes) {
          this.filters.deliveryTypes.forEach(element => {
            target += `&deliveryTypes=${element}`;
          });
        }
        if (this.filters.facilityCodes) {
          this.filters.facilityCodes.forEach(element => {
            target += `&facilityCodes=${element}`;
          });
        }
        else if(barcodeSystemModuleState.currentFacilityCode)
        {
          target += `&facilityCodes=${barcodeSystemModuleState.currentFacilityCode}`;
        }
        if (this.filters.actualFacilityCodes) {
          this.filters.actualFacilityCodes.forEach(element => {
            target += `&actualFacilityCodes=${element}`;
          });
        }
        if (this.filters.billToCompanyIds) {
          this.filters.billToCompanyIds.forEach(element => {
            target += `&billToCompanyIds=${element}`;
          });
        }
        if (this.filters.companyIds) {
          this.filters.companyIds.forEach(element => {
            target += `&companyIds=${element}`;
          });
        }
        if (this.filters.customer) {
          target += `&customer=${this.filters.customer}`;
        }
        if (this.filters.orderGroupText) {
          target += `&orderGroupText=${this.filters.orderGroupText}`;
        }
        if (this.filters.orderGroupText2) {
          target += `&orderGroupText2=${this.filters.orderGroupText2}`;
        }
        if (this.filters.relatedOrdersCRN) {
          target += `&relatedOrdersCRN=${this.filters.relatedOrdersCRN}`;
        }
        if (this.filters.esnIdentity) {
          target += `&esnIdentity=${this.filters.esnIdentity}`;
        }
        if (this.filters.noScanWithItems !== undefined && this.filters.noScanWithItems != null) {
          target += `&noScanWithItems=${this.filters.noScanWithItems}`;
        }
        if (this.filters.isOpen !== undefined && this.filters.isOpen != null) {
          target += `&isOpen=${this.filters.isOpen}`;
        }
        if (this.filters.requiresReceiving !== undefined && this.filters.requiresReceiving != null) {
          target += `&requiresReceiving=${this.filters.requiresReceiving}`;
        }
        if (this.filters.requiresPicking !== undefined && this.filters.requiresPicking != null) {
          target += `&requiresPicking=${this.filters.requiresPicking}`;
        }
        if (this.filters.requiresDelivery !== undefined && this.filters.requiresDelivery != null) {
          target += `&requiresDelivery=${this.filters.requiresDelivery}`;
        }
        if (this.filters.isPuc !== undefined && this.filters.isPuc != null) {
          target += `&isPuc=${this.filters.isPuc}`;
        }
        if (this.filters.isPud !== undefined && this.filters.isPud != null) {
          target += `&requiresReceiving=${this.filters.isPud}`;
        }
        if (this.filters.isLinkingComplete !== undefined && this.filters.isLinkingComplete != null) {
          target += `&isLinkingComplete=${this.filters.isLinkingComplete}`;
        }
        if (this.filters.isPrescheduled !== undefined && this.filters.isPrescheduled != null) {
          target += `&requiresReceiving=${this.filters.isPrescheduled}`;
        }
        if (this.filters.importedDate) {
            const dateData = this.filters.importedDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&importedDateStart=${dateData[0]}&importedDateEnd=${dateData[1]}`;
        }
        if (this.filters.receivedDate) {
            const dateData = this.filters.receivedDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&receivedDateStart=${dateData[0]}&receivedDateEnd=${dateData[1]}`;
        }
        if (this.filters.loadedDate) {
            const dateData = this.filters.loadedDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&loadedDateStart=${dateData[0]}&loadedDateEnd=${dateData[1]}`;
        }
        if (this.filters.deliveredDate) {
            const dateData = this.filters.deliveredDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&deliveredDateStart=${dateData[0]}&deliveredDateEnd=${dateData[1]}`;
        }
        if (this.filters.receivingProcessedDate) {
            const dateData = this.filters.receivingProcessedDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&receivingProcessedDateStart=${dateData[0]}&receivingProcessedDateEnd=${dateData[1]}`;
        }
        if (this.filters.loadingProcessedDate) {
            const dateData = this.filters.loadingProcessedDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&loadingProcessedDateStart=${dateData[0]}&loadingProcessedDateEnd=${dateData[1]}`;
        }
        if (this.filters.deliveryProcessedDate) {
            const dateData = this.filters.deliveryProcessedDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&deliveryProcessedDateStart=${dateData[0]}&deliveryProcessedDateEnd=${dateData[1]}`;
        }
        if (this.filters.earliestAppt) {
            const dateData = this.filters.earliestAppt.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&earliestApptStart=${dateData[0]}&earliestApptEnd=${dateData[1]}`;
        }
        if (this.filters.latestAppt) {
            const dateData = this.filters.latestAppt.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&latestApptStart=${dateData[0]}&latestApptEnd=${dateData[1]}`;
        }
        if (this.filters.originalEarliestAppt) {
            const dateData = this.filters.originalEarliestAppt.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&originalEarliestApptStart=${dateData[0]}&originalEarliestApptEnd=${dateData[1]}`;
        }
        if (this.filters.originalLatestAppt) {
            const dateData = this.filters.originalLatestAppt.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&originalLatestApptStart=${dateData[0]}&originalLatestApptEnd=${dateData[1]}`;
        }
        if (this.filters.receivingUsername) {
          target += `&receivingUsername=${this.filters.receivingUsername}`;
        }
        if (this.filters.loadingUsername) {
          target += `&loadingUsername=${this.filters.loadingUsername}`;
        }
        if (this.filters.deliveryUsername) {
          target += `&deliveryUsername=${this.filters.deliveryUsername}`;
        }
      }
      else if(barcodeSystemModuleState.currentFacilityCode)
      {
          target += `&facilityCodes=${barcodeSystemModuleState.currentFacilityCode}`;
      }

      if (this.dataSortKey) {
        target += `&sortField=${this.dataSortKey}&SortAscending=${
          this.dataSortAscending ? "true" : "false"
        }`;
      }
  
      try {
        var response = await rawApi.get<BarcodeOrderListItem[]>(target);
        this.setList(response);
        this.setCurrentPage(pageNumber);
      } catch (ex) {
        throw ex;
      } finally {
        this.setLoading(false);
      }
    }

    @Action
    async applyListSort(sort: string) {     
      this.setDataSort(sort);
      await this.getBarcodeOrders({
          pageNumber: 1,
          pageSize: this.pageData.pageSize
      });
    }

    @Action
    toggleField(fieldChange) {
      this.setFieldStatus(fieldChange);
    }

    @Action
    async getBarcodeOrder(id): Promise<BarcodeOrderResource> {
      const response = await rawApi.get<BarcodeOrderResource>(`/barcode/orders/${id}`);
      return response.data;
    }

    @Action
    async getBarcodeOrderItems(id): Promise<BarcodeOrderItemListItem[]> {
      const response = await rawApi.get<BarcodeOrderItemListItem[]>(`/barcode/orders/${id}/orderitems`);
      return response.data;
    }

    @Action
    async getBarcodeNoScanLineItems(id): Promise<BarcodeNoScanLineItemListItem[]> {
      const response = await rawApi.get<BarcodeNoScanLineItemListItem[]>(`/barcode/orders/${id}/noscanlineitems`);
      return response.data;
    }


    @Action
    async getBarcodeOrderTransactions(id): Promise<TransactionResouce[]> {
      const response = await rawApi.get<TransactionResouce[]>(`/barcode/orders/${id}/transactions`);
      return response.data;
    }

    @Action
    async getBarcodeOrderItemPicks(id): Promise<BarcodeOrderItemPickListItem[]> {
      const response = await rawApi.get<BarcodeOrderItemPickListItem[]>(`/barcode/orders/${id}/orderitempicks?isPicked=false`);
      return response.data;
    }

    @Action
    async getBarcodeOrderPallets(id): Promise<PalletListItem[]> {
      const response = await rawApi.get<PalletListItem[]>(`/barcode/orders/${id}/pallets`);
      return response.data;
    }

    @Action
    async getBarcodeOrderStockPickings(id): Promise<StockPickingListItem[]> {
      const response = await rawApi.get<StockPickingListItem[]>(`/barcode/orders/${id}/stockPicking?isPicked=false`);
      return response.data;
    }

    @Action
    async getBarcodeOrderDeliverySignatures(id): Promise<DeliverySignatureResource[]> {
      const response = await rawApi.get<DeliverySignatureResource[]>(`/barcode/orders/${id}/deliverySignatures`);
      return response.data;
    }

    @Action
    async previewUpdateBarcodeOrderItem(data : BarcodeOrderItemListItem): Promise<UpdateBarcodeOrderItemRequest> {
      const response = await rawApi.get<UpdateBarcodeOrderItemRequest>(`/barcode/orders/${data.orderId}/orderItems/${data.id}/previewUpdate`)
      return response.data;
    }

    @Action
    async updateBarcodeOrderItem(request: UpdateBarcodeOrderItemRequest) {
      if(!request.allowQuantityReceivedChange)
        request.quantityReceived = undefined;
      if(!request.allowQuantityLoadedChange)
        request.quantityLoaded = undefined;
      if(!request.allowQuantityDeliveredChange)
        request.quantityDelivered = undefined;
      await api.putAsync(`/barcode/orders/${request.orderId}/orderItems/${request.orderItemId}`, request);
    } 

    @Action
    async previewUpdateBarcodeNoScanLineItem(data : BarcodeNoScanLineItemListItem): Promise<UpdateBarcodeNoScanLineItemRequest> {
      const response = await rawApi.get<UpdateBarcodeNoScanLineItemRequest>(`/barcode/orders/${data.orderId}/noscanlineitems/${data.id}/previewUpdate`)
      return response.data;
    }

    @Action
    async updateBarcodeNoScanLineItem(request: UpdateBarcodeNoScanLineItemRequest) {
      await api.putAsync(`/barcode/orders/${request.orderId}/noscanlineitems/${request.id}`, request);
    } 

    @Action
    async previewCreateBarcodeNoScanLineItem(orderId: number): Promise<CreateBarcodeNoScanLineItemRequest> {
      const response = await rawApi.get<CreateBarcodeNoScanLineItemRequest>(`/barcode/orders/${orderId}/noscanlineitems/previewadd`)
      return response.data;
    }

    @Action
    async createBarcodeNoScanLineItem(request: CreateBarcodeNoScanLineItemRequest) {
      await api.postAsync(`/barcode/orders/${request.orderId}/noscanlineitems`, request);
    } 

    @Action
    async previewDeleteBarcodeNoScanLineItem(data : BarcodeNoScanLineItemListItem): Promise<DeleteBarcodeNoScanLineItemRequest> {
      const response = await rawApi.get<DeleteBarcodeNoScanLineItemRequest>(`/barcode/orders/${data.orderId}/noscanlineitems/${data.id}/previewDelete`)
      return response.data;
    }

    @Action
    async deleteBarcodeNoScanLineItem(data : BarcodeNoScanLineItemListItem) {
      await api.deleteAsync(`/barcode/orders/${data.orderId}/noscanlineitems/${data.id}`);
    } 

    @Action
    async previewReceiveBarcodeOrder(id): Promise<ReceiveBarcodeOrderRequest> {
      const response = await rawApi.get<ReceiveBarcodeOrderRequest>(`/barcode/orders/${id}/receive`);
      return response.data;
    }

    @Action
    async receiveBarcodeOrder(request: ReceiveBarcodeOrderRequest) {
      await api.postAsync(`barcode/orders/${request.id}/receive`, request);
    } 

    @Action
    async previewDeliverBarcodeOrder(id): Promise<DeliverBarcodeOrderRequest> {
      const response = await rawApi.get<DeliverBarcodeOrderRequest>(`/barcode/orders/${id}/deliver`);
      return response.data;
    }

    @Action
    async deliverBarcodeOrder(request: DeliverBarcodeOrderRequest) {
      await api.postAsync(`barcode/orders/${request.id}/deliver`, request);
    } 

    @Action
    async previewRereceiveBarcodeOrder(id): Promise<RereceiveBarcodeOrderRequest> {
      const response = await rawApi.get<RereceiveBarcodeOrderRequest>(`/barcode/orders/${id}/rereceive`);
      return response.data;
    }

    @Action
    async rereceiveBarcodeOrder(request: RereceiveBarcodeOrderRequest) {
      await api.postAsync(`barcode/orders/${request.id}/rereceive`, request);
    }

    @Action
    async createBarcodeOrderFromHeaderNumber(request: CreateOrderFromHeaderNumberRequest) {
      return await api.postAsync('barcode/orders', request);
    }

    @Action
    async previewLinkInventory(id): Promise<LinkInventoryRequest> {
      const response = await rawApi.get<LinkInventoryRequest>(`/barcode/orders/${id}/linkInventory`);
      return response.data;
    }

    @Action
    async linkInventory(request: LinkInventoryRequest) {
      await api.postAsync(`barcode/orders/${request.id}/linkInventory`, request);
    } 

    @Action
    async getDeliverySignatureImage(signature: DeliverySignatureResource) : Promise<any> {
      const response = await rawApi.get<any>(`barcode/orders/${signature.orderId}/deliverysignatures/signature_${signature.id}.jpg`, {
        responseType: "blob"
      });
      return response.data;
    } 

    @Action
    async getOrderItemGps(barcodeOrderId) : Promise<OrderItemGps[]> {
      const response = await rawApi.get<OrderItemGps[]>(`barcode/orders/${barcodeOrderId}/orderItemGps`);
      return response.data;
    }

    @Action
    async getRequiredStock(orderId: number): Promise<RequiredStockItemResource[]> {
      const response = await rawApi.get<RequiredStockItemResource[]>(`barcode/orders/${orderId}/requiredStock`);
      return response.data;
    }

    @Action
    async saveRequiredStock(request: SaveRequiredStockRequest) {
      await api.postAsync(`barcode/orders/${request.orderId}/requiredStock`, request);
    }

    @Action 
    resetData() {
        this.resetList();
    }

    @Action
    async loadBarcodeOrder(id: number): Promise<void> {
      const data = await this.getBarcodeOrder(id);
      this.setCurrentBarcodeOrder(data);
      this.refreshBarcodeOrderData(data);
    }   
     
    @Action
    async loadBarcodeOrderItems(id: number): Promise<void> {
      this.setCurrentBarcodeOrderItems(await this.getBarcodeOrderItems(id));
    } 

    @Action
    async loadBarcodeNoScanLineItems(id: number): Promise<void> {
      this.setCurrentBarcodeNoScanLineItems(await this.getBarcodeNoScanLineItems(id));
    } 

    @Action
    async loadBarcodeOrderItemPicks(id: number): Promise<void> {
      this.setCurrentBarcodeOrderItemPicks(await this.getBarcodeOrderItemPicks(id));
    } 
    
    @Action
    async loadBarcodeOrderPallets(id: number): Promise<void> {
      this.setCurrentBarcodeOrderPallets(await this.getBarcodeOrderPallets(id));
    }

    @Action
    async loadBarcodeOrderStockPickings(id: number): Promise<void> {
      this.setCurrentBarcodeOrderStockPickings(await this.getBarcodeOrderStockPickings(id));
    }

    @Action
    async loadBarcodeOrderDeliverySignatures(id: number): Promise<void> {
      let deliverySignatures = await this.getBarcodeOrderDeliverySignatures(id);

      if(deliverySignatures)
      {
        for(let index = 0; index < deliverySignatures.length; index++){
          try {
            let blob = await this.getDeliverySignatureImage(deliverySignatures[index]);
            deliverySignatures[index].src = URL.createObjectURL(new Blob([blob]));
          } catch {
            deliverySignatures[index].src = '';
          }         
        }
      }

      this.setCurrentBarcodeOrderDeliverySignatures(deliverySignatures);   
    }

    @Action
    async loadBarcodeOrderTransactions(id: number): Promise<void> {
      this.setCurrentBarcodeOrderTransactions(await this.getBarcodeOrderTransactions(id));
    } 
  
    @Action
    async reloadBarcodeOrder(): Promise<void> {
      if (this.currentBarcodeOrder)
      {
        const data = await this.getBarcodeOrder(this.currentBarcodeOrder.id);
        this.setCurrentBarcodeOrder(data);
        this.refreshBarcodeOrderData(data);
      } 
    }
 
    @Action
    orderPreviewShortcutAction(payload) {
      this.context.dispatch(
        "system/showModal",
        { name: modals.orderPreviewModal, data: payload.selectedItems[0].orderNumber },
        { root: true }
      );
    }

    @Action
    openOrderShortcutAction(payload) {
      if (payload && payload.selectedItems) {
        router.push({ path: "/order/" + payload.selectedItems[0].orderNumber });
      } else {
        console.warn("no selected items");
      }
    }

    @Action
    async viewBarcodeOrderShortcutAction(payload) {
      if (!payload || !payload.selectedItems) {
        return;
      }
      return this.context.dispatch(
        "system/showModal",
        { name: modals.barcodeOrderViewModal, data: { selectedItems: payload.selectedItems } },
        { root: true }
      );
    } 
    
    @Action
    async linkInventoryToOrderShortcutAction(payload) {
      if (!payload || !payload.selectedItems) {
        return;
      }
      if(payload.selectedItems.length == 1)
      {
        if(payload.selectedItems[0].barcodeOrderId) {
          return this.context.dispatch(
            "system/showModal",
            { name: modals.linkInventoryToOrderModal, data: { selectedItems: [{id: payload.selectedItems[0].barcodeOrderId}]}},
            { root: true }
          );
        } else {
          return this.context.dispatch(
            "system/showModal",
            { name: modals.createBarcodeOrderFromHeaderNumberModal, data: { selectedItems: payload.selectedItems }},
            { root: true }
          );
        }    
      }     
    }

    @Action
    async receiveBarcodeOrderShortcutAction(payload) {
      if (!payload || !payload.selectedItems) {
        return;
      }
      return this.context.dispatch(
        "system/showModal",
        { name: modals.receiveBarcodeOrderModal, data: { selectedItems: payload.selectedItems } },
        { root: true }
      );
    }
    
    @Action
    async deliverBarcodeOrderShortcutAction(payload) {
      if (!payload || !payload.selectedItems) {
        return;
      }
      return this.context.dispatch(
        "system/showModal",
        { name: modals.deliverBarcodeOrderModal, data: { selectedItems: payload.selectedItems } },
        { root: true }
      );
    }

    @Action
    async updateBarcodeOrdersAfterAction(items: any) {
      for (let index = 0; index < items.length; index++) {
        const order = await this.getBarcodeOrder(items[index].id);
        this.refreshBarcodeOrderData(order)
      }
    }

    @Mutation
    setLoading(isLoading: boolean) {
        this.loading = isLoading;
    }

    @Mutation
    setFilters(filters: BarcodeOrderListFilters)
    {
        this.filters = filters;
    }

    @Mutation
    setList(response: AxiosResponse<BarcodeOrderListItem[]>) {
        this.pageData.totalItems = response.headers.totalitems;
        this.pageData.totalPages = response.headers.totalpages;
        if (this.data) {
            this.data = this.data.concat(response.data);
        } else {
            this.data = response.data;
        }
    }  
    
    @Mutation
    setCurrentPage(number: number) {
      this.pageData.currentPage = number;
    }

    @Mutation
    resetList() {
      this.pageData.totalItems = 0;
      this.pageData.totalPages = 0;
      this.data = null;
    }

    @Mutation
    switchSelectionStatus(id: number) {
      var index = this.data.findIndex(function(item) {
          return item.id === id;
      });

      if (index < 0) {
          return;
      }

      const existing = this.data[index];

      existing.selected = !existing.selected;

      this.data = [...this.data.slice(0, index), existing, ...this.data.slice(index + 1)];
    }  

    @Mutation
    switchBarcodeOrderItemSelectionStatus(id: number) {
      if(this.currentBarcodeOrderItems)
      {
        var index = this.currentBarcodeOrderItems.findIndex(function(item) {
          return item.id === id;
        });

        if (index < 0) {
            return;
        }

        const existing = this.currentBarcodeOrderItems[index];

        existing.selected = !existing.selected;

        this.currentBarcodeOrderItems = [...this.currentBarcodeOrderItems.slice(0, index), existing, ...this.currentBarcodeOrderItems.slice(index + 1)];
      }    
    }

    @Mutation
    switchBarcodeNoScanLineItemSelectionStatus(id: number) {
      if(this.currentBarcodeNoScanLineItems)
      {
        var index = this.currentBarcodeNoScanLineItems.findIndex(function(item) {
          return item.id === id;
        });

        if (index < 0) {
            return;
        }

        const existing = this.currentBarcodeNoScanLineItems[index];

        existing.selected = !existing.selected;

        this.currentBarcodeNoScanLineItems = [...this.currentBarcodeNoScanLineItems.slice(0, index), existing, ...this.currentBarcodeNoScanLineItems.slice(index + 1)];
      }    
    }

    

    @Mutation
    switchBarcodeOrderItemPickSelectionStatus(id: number) {
      if(this.currentBarcodeOrderItemPicks)
      {
        var index = this.currentBarcodeOrderItemPicks.findIndex(function(item) {
          return item.id === id;
        });

        if (index < 0) {
            return;
        }

        const existing = this.currentBarcodeOrderItemPicks[index];

        existing.selected = !existing.selected;

        this.currentBarcodeOrderItemPicks = [...this.currentBarcodeOrderItemPicks.slice(0, index), existing, ...this.currentBarcodeOrderItemPicks.slice(index + 1)];
      }    
    }

    @Mutation
    switchBarcodeOrderPalletSelectionStatus(id: string) {
      if(this.currentBarcodeOrderPallets)
      {
        var index = this.currentBarcodeOrderPallets.findIndex(function(item) {
          return item.id === id;
        });

        if (index < 0) {
            return;
        }

        const existing = this.currentBarcodeOrderPallets[index];

        existing.selected = !existing.selected;

        this.currentBarcodeOrderPallets = [...this.currentBarcodeOrderPallets.slice(0, index), existing, ...this.currentBarcodeOrderPallets.slice(index + 1)];
      }    
    }

    @Mutation
    switchBarcodeOrderStockPickingSelectionStatus(id: number) {
      if(this.currentBarcodeOrderStockPickings)
      {
        var index = this.currentBarcodeOrderStockPickings.findIndex(function(item) {
          return item.id === id;
        });

        if (index < 0) {
            return;
        }

        const existing = this.currentBarcodeOrderStockPickings[index];

        existing.selected = !existing.selected;

        this.currentBarcodeOrderStockPickings = [...this.currentBarcodeOrderStockPickings.slice(0, index), existing, ...this.currentBarcodeOrderStockPickings.slice(index + 1)];
      }    
    }

    @Mutation
    refreshBarcodeOrderData(load: BarcodeOrderListItem) {
      if(this.data)
      {
        var index = this.data.findIndex(function(item) {
            return item.id === load.id;
        });

        if (index < 0) {
            return;
        }

        this.data = [...this.data.slice(0, index), load, ...this.data.slice(index + 1)]
      }
    } 
    
    @Mutation
    setCurrentBarcodeOrder(data) {
      this.loading = false;
      this.currentBarcodeOrderItems = [];
      this.currentBarcodeNoScanLineItems = [];
      this.currentBarcodeOrderItemPicks = [];
      this.currentBarcodeOrderPallets = [];
      this.currentBarcodeOrderStockPickings = [];
      this.currentBarcodeDeliverySignatures = [];
      this.currentBarcodeOrderTransactions = [];
      this.currentBarcodeOrder = data;
    }    
    
    @Mutation
    setCurrentBarcodeOrderItems(data) {
      this.loading = false;
      this.currentBarcodeOrderItems = data;
    } 

    @Mutation
    setCurrentBarcodeNoScanLineItems(data) {
      this.loading = false;
      this.currentBarcodeNoScanLineItems = data;
    } 

    @Mutation
    setCurrentBarcodeOrderItemPicks(data) {
      this.loading = false;
      this.currentBarcodeOrderItemPicks = data;
    } 

    @Mutation
    setCurrentBarcodeOrderPallets(data) {
      this.loading = false;
      this.currentBarcodeOrderPallets = data;
    }
    
    @Mutation
    setCurrentBarcodeOrderStockPickings(data) {
      this.loading = false;
      this.currentBarcodeOrderStockPickings = data;
    }

    @Mutation
    setCurrentBarcodeOrderDeliverySignatures(data) {
      this.loading = false;
      this.currentBarcodeDeliverySignatures = data;
    }

    @Mutation
    setCurrentBarcodeOrderTransactions(data) {
      this.loading = false;
      this.currentBarcodeOrderTransactions = data;
    }

    @Mutation
    resetCurrentBarcodeOrder() {
      this.currentBarcodeOrder = defaultBarcodeOrder();
      this.currentBarcodeOrderItems = [];
      this.currentBarcodeOrderItemPicks = [];
      this.currentBarcodeOrderPallets = [];
      this.currentBarcodeOrderStockPickings = [];
      this.currentBarcodeDeliverySignatures = [];
      this.currentBarcodeOrderTransactions = [];
    }

    @Mutation
    setFieldStatus(loadFieldChange) {
      this.fields[loadFieldChange.idx].hidden = loadFieldChange.value;
    }

    @Mutation
    setDataSort(key) {
      if (this.dataSortKey === key) this.dataSortAscending = !this.dataSortAscending;
      else {
        this.dataSortKey = key;
        this.dataSortAscending = true;
      }
    }
    

    get getSelectedItems(): Array<BarcodeOrderListItem> {
        if (!this.data) return [];
        return this.data.filter(x => x.selected);
    }

    get getSelectedItemCount(): number {
        return this.getSelectedItems.length;
    }

    get getSelectedBarcodeOrderItems(): Array<BarcodeOrderItemListItem> {
      if (!this.currentBarcodeOrderItems) return [];
      return this.currentBarcodeOrderItems.filter(x => x.selected);
    }

    get getSelectedBarcodeNoScanLineItems(): Array<BarcodeNoScanLineItemListItem> {
      if (!this.currentBarcodeNoScanLineItems) return [];
      return this.currentBarcodeNoScanLineItems.filter(x => x.selected);
    }

    get getSelectedBarcodeOrderItemCount(): number {
      return this.getSelectedBarcodeOrderItems.length;
    }

    get getSelectedBarcodeNoScanLineItemCount(): number {
      return this.getSelectedBarcodeNoScanLineItems.length;
    }

    
    get getSelectedBarcodeOrderItemPicks(): Array<BarcodeOrderItemPickListItem> {
      if (!this.currentBarcodeOrderItemPicks) return [];
      return this.currentBarcodeOrderItemPicks.filter(x => x.selected);
    }

    get getSelectedBarcodeOrderItemPickCount(): number {
      return this.getSelectedBarcodeOrderItemPicks.length;
    }

    get getSelectedBarcodeOrderPallets(): Array<PalletListItem> {
      if (!this.currentBarcodeOrderPallets) return [];
      return this.currentBarcodeOrderPallets.filter(x => x.selected);
    }

    get getSelectedBarcodeOrderPalletCount(): number {
      return this.getSelectedBarcodeOrderPallets.length;
    }

    get getSelectedBarcodeOrderStockPickings(): Array<StockPickingListItem> {
      if (!this.currentBarcodeOrderStockPickings) return [];
      return this.currentBarcodeOrderStockPickings.filter(x => x.selected);
    }

    get getSelectedBarcodeOrderStockPickingCount(): number {
      return this.getSelectedBarcodeOrderStockPickings.length;
    }
}